import React, { useMemo } from 'react';
import { Text } from '@/components/ui/text/Text';
import { Selector } from '@/components/ui/inputs/selector';
import { Skeleton } from '@/components/ui/loading';
import { ActionMenu, type ActionMenuItem } from '@/components/ui/menus';
import { PermissionGuard } from '@/components/layout';
import { useTranslation } from '@/i18n';
import {
  useMonthSummaryDetailNavigation,
  useMonthSummaryAbsencesDetailNavigation,
  useMonthSummaryMarksDetailNavigation,
} from '@/hooks/attendance/dashboard';
import { DonutSummaryCard } from './DonutSummaryCard';
import { DonutSummaryCardSkeleton } from './DonutSummaryCardSkeleton';
import { getOvertimeColor, getTardinessColor, getAbsenceColor } from './attendanceThresholds';
import { colors } from '@/assets/styles/colors';

// Tailwind requiere valores arbitrarios estáticos en el código fuente, por lo que no puede
// interpolar colors.primary.general (#B2E5FF) / colors.primary.main (#1E5591) en runtime.
const CUT_FILTER_SELECTOR_CLASS_NAME =
  '!h-6 !rounded-full !border-0 !bg-[#B2E5FF] !text-[#1E5591] !pl-2 !pr-8 !py-0 flex items-center gap-2';

export interface MonthSummaryData {
  overtime: {
    percentage: number;
    approved: string;
    pending: string;
    total: string;
  };
  tardiness: {
    percentage: number;
    approved: string;
    pending: string;
    total: string;
  };
  absences: {
    percentage: number;
    unjustified: string;
    justified: string;
    total: string;
  };
  marks: {
    percentageInPerson: number;
    percentageRemote: number;
  };
}

interface MonthSummarySectionProps {
  data?: MonthSummaryData | null;
  isLoadingData?: boolean;
  selectedYear: string;
  selectedMonth: string;
  selectedCutId: string;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  cutOptions: { value: string; label: string }[];
  isCutSelectorDisabled: boolean;
  onYearChange: (year: string) => void;
  onMonthChange: (month: string) => void;
  onCutChange: (cutId: string) => void;
  isLoadingFilters?: boolean;
  selectedCompanyId: string;
  onCompanyChange: (companyId: string) => void;
  companyOptions: { value: string; label: string }[];
  'data-testid'?: string;
}

export const MonthSummarySection: React.FC<MonthSummarySectionProps> = ({
  data,
  isLoadingData = false,
  selectedYear,
  selectedMonth,
  selectedCutId,
  yearOptions,
  monthOptions,
  cutOptions,
  isCutSelectorDisabled,
  onYearChange,
  onMonthChange,
  onCutChange,
  isLoadingFilters = false,
  selectedCompanyId,
  onCompanyChange,
  companyOptions,
  'data-testid': dataTestId,
}) => {
  const { t: translate } = useTranslation();
  const hoursUnit = translate('attendance:dashboard.monthSummary.units.hours');
  const daysUnit = translate('attendance:dashboard.monthSummary.units.days');

  const { navigateToDetail } = useMonthSummaryDetailNavigation(selectedCompanyId, selectedCutId);
  const { navigateToDetail: navigateToAbsencesDetail } =
    useMonthSummaryAbsencesDetailNavigation(selectedCompanyId, selectedCutId);
  const { navigateToDetail: navigateToMarksDetail } =
    useMonthSummaryMarksDetailNavigation(selectedCompanyId, selectedCutId);

  const horasExtraMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewDetail',
      label: translate('attendance:dashboard.monthSummary.horasExtra.menu.detalle'),
      icon: 'eye',
      color: colors.important.main,
      onClick: () => navigateToDetail('HHEE'),
    },
  ], [translate, navigateToDetail]);

  const atrasosMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewDetail',
      label: translate('attendance:dashboard.monthSummary.atrasos.menu.detalle'),
      icon: 'eye',
      color: colors.important.main,
      onClick: () => navigateToDetail('Atrasos'),
    },
  ], [translate, navigateToDetail]);

  const ausenciasMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewDetail',
      label: translate('attendance:dashboard.monthSummary.ausencias.menu.detalle'),
      icon: 'eye',
      color: colors.important.main,
      onClick: () => void navigateToAbsencesDetail(),
    },
  ], [translate, navigateToAbsencesDetail]);

  const marcajesMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewDetail',
      label: translate('attendance:dashboard.monthSummary.marcajes.menu.detalle'),
      icon: 'eye',
      color: colors.important.main,
      onClick: () => void navigateToMarksDetail(),
    },
  ], [translate, navigateToMarksDetail]);

  const handleCompanyChange = (value: string | string[]) => {
    onCompanyChange(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  const handleYearChange = (value: string | string[]) => {
    onYearChange(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  const handleMonthChange = (value: string | string[]) => {
    onMonthChange(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  const handleCutChange = (value: string | string[]) => {
    onCutChange(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }} data-testid={dataTestId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Text
          variant="body-medium"
          fontFamily="montserrat"
          color={colors.grays.neutral66}
          style={{ fontWeight: 600 }}
          data-testid={`${dataTestId}-title`}
        >
          {translate('attendance:dashboard.monthSummary.title')}
        </Text>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isLoadingFilters ? (
            <>
              <Skeleton width={196} height={24} className="rounded-full" data-testid={`${dataTestId}-company-skeleton`} />
              <Skeleton width={196} height={24} className="rounded-full" data-testid={`${dataTestId}-year-skeleton`} />
              <Skeleton width={196} height={24} className="rounded-full" data-testid={`${dataTestId}-month-skeleton`} />
              <Skeleton width={196} height={24} className="rounded-full" data-testid={`${dataTestId}-cut-skeleton`} />
            </>
          ) : (
            <>
              {companyOptions.length > 0 && (
                <Selector
                  label={translate('attendance:dashboard.monthSummary.filters.company')}
                  placeholder={translate('attendance:dashboard.monthSummary.filters.company')}
                  options={companyOptions}
                  value={selectedCompanyId || undefined}
                  onChange={handleCompanyChange}
                  disabled={companyOptions.length <= 1}
                  hideLabel
                  chevronColor={colors.primary.main}
                  chevronWrapperClassName="items-center"
                  className="w-[196px]"
                  selectClassName={CUT_FILTER_SELECTOR_CLASS_NAME}
                  data-testid={`${dataTestId}-company-selector`}
                />
              )}
              <Selector
                label={translate('attendance:dashboard.monthSummary.filters.year')}
                placeholder={translate('attendance:dashboard.monthSummary.filters.year')}
                options={yearOptions}
                value={selectedYear || undefined}
                onChange={handleYearChange}
                hideLabel
                chevronColor={colors.primary.main}
                chevronWrapperClassName="items-center"
                className="w-[196px]"
                selectClassName={CUT_FILTER_SELECTOR_CLASS_NAME}
                data-testid={`${dataTestId}-year-selector`}
              />
              <Selector
                label={translate('attendance:dashboard.monthSummary.filters.month')}
                placeholder={translate('attendance:dashboard.monthSummary.filters.month')}
                options={monthOptions}
                value={selectedMonth || undefined}
                onChange={handleMonthChange}
                hideLabel
                chevronColor={colors.primary.main}
                chevronWrapperClassName="items-center"
                className="w-[196px]"
                selectClassName={CUT_FILTER_SELECTOR_CLASS_NAME}
                data-testid={`${dataTestId}-month-selector`}
              />
              <Selector
                label={translate('attendance:dashboard.monthSummary.filters.cut')}
                placeholder={translate('attendance:dashboard.monthSummary.filters.cut')}
                options={cutOptions}
                value={selectedCutId || undefined}
                onChange={handleCutChange}
                disabled={isCutSelectorDisabled}
                hideLabel
                chevronColor={colors.primary.main}
                chevronWrapperClassName="items-center"
                className="w-[196px]"
                selectClassName={CUT_FILTER_SELECTOR_CLASS_NAME}
                data-testid={`${dataTestId}-cut-selector`}
              />
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {(isLoadingData || !data) ? (
          <>
            <DonutSummaryCardSkeleton data-testid={`${dataTestId}-horas-extra-skeleton`} />
            <DonutSummaryCardSkeleton data-testid={`${dataTestId}-atrasos-skeleton`} />
            <DonutSummaryCardSkeleton data-testid={`${dataTestId}-ausencias-skeleton`} />
            <DonutSummaryCardSkeleton rows={2} data-testid={`${dataTestId}-marcajes-skeleton`} />
          </>
        ) : (
          <>
            <DonutSummaryCard
              title={translate('attendance:dashboard.monthSummary.horasExtra.title')}
              percentage={data.overtime.percentage}
              color={getOvertimeColor(data.overtime.percentage)}
              percentageLabel={`${data.overtime.percentage}%`}
              rows={[
                { label: translate('attendance:dashboard.monthSummary.horasExtra.approved'), value: `${data.overtime.approved} ${hoursUnit}`, valueColor: getOvertimeColor(data.overtime.percentage) },
                { label: translate('attendance:dashboard.monthSummary.horasExtra.pending'), value: `${data.overtime.pending} ${hoursUnit}` },
                { label: translate('attendance:dashboard.monthSummary.horasExtra.total'), value: `${data.overtime.total} ${hoursUnit}` },
              ]}
              headerAction={(
                <PermissionGuard action="view" fallback="hidden">
                  <ActionMenu
                    items={horasExtraMenuItems}
                    triggerIcon="moreVertical"
                    placement="right"
                    ariaLabel={translate('attendance:dashboard.monthSummary.horasExtra.menu.ariaLabel')}
                  />
                </PermissionGuard>
              )}
              data-testid={`${dataTestId}-horas-extra`}
            />

            <DonutSummaryCard
              title={translate('attendance:dashboard.monthSummary.atrasos.title')}
              percentage={data.tardiness.percentage}
              color={getTardinessColor(data.tardiness.percentage)}
              percentageLabel={`${data.tardiness.percentage}%`}
              rows={[
                { label: translate('attendance:dashboard.monthSummary.atrasos.approved'), value: `${data.tardiness.approved} ${hoursUnit}`, valueColor: getTardinessColor(data.tardiness.percentage) },
                { label: translate('attendance:dashboard.monthSummary.atrasos.pending'), value: `${data.tardiness.pending} ${hoursUnit}` },
                { label: translate('attendance:dashboard.monthSummary.atrasos.total'), value: `${data.tardiness.total} ${hoursUnit}` },
              ]}
              headerAction={(
                <PermissionGuard action="view" fallback="hidden">
                  <ActionMenu
                    items={atrasosMenuItems}
                    triggerIcon="moreVertical"
                    placement="right"
                    ariaLabel={translate('attendance:dashboard.monthSummary.atrasos.menu.ariaLabel')}
                  />
                </PermissionGuard>
              )}
              data-testid={`${dataTestId}-atrasos`}
            />

            <DonutSummaryCard
              title={translate('attendance:dashboard.monthSummary.ausencias.title')}
              percentage={data.absences.percentage}
              color={getAbsenceColor(data.absences.percentage)}
              percentageLabel={`${data.absences.percentage}%`}
              rows={[
                { label: translate('attendance:dashboard.monthSummary.ausencias.unjustified'), value: `${data.absences.unjustified} ${daysUnit}`, valueColor: getAbsenceColor(data.absences.percentage) },
                { label: translate('attendance:dashboard.monthSummary.ausencias.justified'), value: `${data.absences.justified} ${daysUnit}` },
                { label: translate('attendance:dashboard.monthSummary.ausencias.total'), value: `${data.absences.total} ${daysUnit}` },
              ]}
              headerAction={(
                <PermissionGuard action="view" fallback="hidden">
                  <ActionMenu
                    items={ausenciasMenuItems}
                    triggerIcon="moreVertical"
                    placement="right"
                    ariaLabel={translate('attendance:dashboard.monthSummary.ausencias.menu.ariaLabel')}
                  />
                </PermissionGuard>
              )}
              data-testid={`${dataTestId}-ausencias`}
            />

            <DonutSummaryCard
              title={translate('attendance:dashboard.monthSummary.marcajes.title')}
              segments={[
                { percentage: data.marks.percentageInPerson, color: colors.primary.main },
                { percentage: data.marks.percentageRemote, color: colors.dashboardStatus.remoto },
              ]}
              labelColor={colors.primary.main}
              percentageLabel={`${data.marks.percentageInPerson}%`}
              percentageSubLabel={translate('attendance:dashboard.monthSummary.marcajes.presencial')}
              rows={[
                {
                  label: translate('attendance:dashboard.monthSummary.marcajes.presencial'),
                  value: `${data.marks.percentageInPerson} %`,
                  valueColor: colors.primary.main,
                },
                {
                  label: translate('attendance:dashboard.monthSummary.marcajes.remoto'),
                  value: `${data.marks.percentageRemote} %`,
                  valueColor: colors.dashboardStatus.remoto,
                },
              ]}
              headerAction={(
                <PermissionGuard action="view" fallback="hidden">
                  <ActionMenu
                    items={marcajesMenuItems}
                    triggerIcon="moreVertical"
                    placement="right"
                    ariaLabel={translate('attendance:dashboard.monthSummary.marcajes.menu.ariaLabel')}
                  />
                </PermissionGuard>
              )}
              data-testid={`${dataTestId}-marcajes`}
            />
          </>
        )}
      </div>
    </div>
  );
};
